import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'hxaro-language';
const DEFAULT_LOCALE = 'en';
const SUPPORTED_LOCALES = ['en', 'de', 'es'];
const OG_LOCALE_BY_LOCALE = {
  en: 'en_US',
  de: 'de_DE',
  es: 'es_ES',
};

const metadataByLocale = {
  en: {
    title: 'The Great Divergence: From Hxaro to Market Competition — 100,000 Years of Economic Evolution',
    description: 'An interactive pitch deck exploring how humanity shifted from collaborative gift economies (Hxaro) to competition-first market systems over 100,000 years.',
  },
  de: {
    title: 'Die große Divergenz: Von Hxaro zu Marktwettbewerb — 100.000 Jahre Wirtschaftsentwicklung',
    description: 'Eine interaktive Erzählung darüber, wie sich die Menschheit von kollaborativen Geschenkökonomien zu wettbewerbsorientierten Marktsystemen entwickelte.',
  },
  es: {
    title: 'La Gran Divergencia: De Hxaro a la competencia de mercado — 100.000 anos de evolucion economica',
    description: 'Una historia interactiva sobre como la humanidad paso de economias del don colaborativas a sistemas de mercado centrados en la competencia.',
  },
};

function updateMetaTag(selector, content) {
  if (!content || typeof document === 'undefined') return;
  const tag = document.querySelector(selector);
  if (tag) tag.setAttribute('content', content);
}

export const languageOptions = [
  { value: 'en', label: 'English', nativeLabel: 'English' },
  { value: 'de', label: 'German', nativeLabel: 'Deutsch' },
  { value: 'es', label: 'Spanish', nativeLabel: 'Espanol' },
];

function normalizeLocale(value) {
  if (typeof value !== 'string') return DEFAULT_LOCALE;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return DEFAULT_LOCALE;

  const direct = trimmed.slice(0, 2);
  return SUPPORTED_LOCALES.includes(direct) ? direct : DEFAULT_LOCALE;
}

function readStoredLocale() {
  if (typeof window === 'undefined') return null;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) return normalizeLocale(stored);
  } catch {
    // no-op
  }

  return null;
}

function readBrowserLocale() {
  if (typeof navigator === 'undefined') return null;

  const localeCandidates = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : [navigator.language];

  for (const candidate of localeCandidates) {
    if (typeof candidate !== 'string') continue;
    const direct = candidate.trim().toLowerCase().slice(0, 2);
    if (SUPPORTED_LOCALES.includes(direct)) return direct;
  }

  return null;
}

function readLocaleFromUrl() {
  if (typeof window === 'undefined') return null;

  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get('lang');
    if (!value) return null;
    return normalizeLocale(value);
  } catch {
    return null;
  }
}

function readInitialLocale() {
  const fromUrl = readLocaleFromUrl();
  if (fromUrl && SUPPORTED_LOCALES.includes(fromUrl)) return fromUrl;

  const fromStorage = readStoredLocale();
  if (fromStorage && SUPPORTED_LOCALES.includes(fromStorage)) return fromStorage;

  const fromBrowser = readBrowserLocale();
  if (fromBrowser && SUPPORTED_LOCALES.includes(fromBrowser)) return fromBrowser;

  return DEFAULT_LOCALE;
}

function syncLocaleInUrl(locale) {
  if (typeof window === 'undefined') return;

  try {
    const url = new URL(window.location.href);
    if (locale === DEFAULT_LOCALE) {
      url.searchParams.delete('lang');
    } else {
      url.searchParams.set('lang', locale);
    }

    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl !== currentUrl) {
      window.history.replaceState({}, '', nextUrl);
    }
  } catch {
    // no-op
  }
}

export const LanguageContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  isLocaleSupported: () => false,
});

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(() => readInitialLocale());

  const setLocale = useCallback((nextLocale) => {
    setLocaleState(normalizeLocale(nextLocale));
  }, []);

  useEffect(() => {
    const metadata = metadataByLocale[locale] || metadataByLocale.en;

    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
      document.title = metadata.title;

      updateMetaTag('meta[name="description"]', metadata.description);
      updateMetaTag('meta[property="og:title"]', metadata.title);
      updateMetaTag('meta[property="og:description"]', metadata.description);
      updateMetaTag('meta[property="og:locale"]', OG_LOCALE_BY_LOCALE[locale] || OG_LOCALE_BY_LOCALE.en);
      updateMetaTag('meta[name="twitter:title"]', metadata.title);
      updateMetaTag('meta[name="twitter:description"]', metadata.description);
    }

    if (typeof window !== 'undefined') {
      syncLocaleInUrl(locale);

      try {
        window.localStorage.setItem(STORAGE_KEY, locale);
      } catch {
        // no-op
      }
    }
  }, [locale]);

  const value = useMemo(() => ({
    locale,
    setLocale,
    isLocaleSupported: (candidate) => SUPPORTED_LOCALES.includes(normalizeLocale(candidate)),
  }), [locale, setLocale]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export function pickLocaleCopy(copyByLocale, locale) {
  if (!copyByLocale || typeof copyByLocale !== 'object') return copyByLocale;
  return copyByLocale[locale] || copyByLocale[DEFAULT_LOCALE] || Object.values(copyByLocale)[0];
}
